import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HelpCircle, ArrowRight, AlertTriangle, CheckCircle2, Info, ChevronRight } from "lucide-react";

export function ExplainButton({ onClick, size = "sm" }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 text-cyan-400/70 hover:text-cyan-400 transition-colors"
      data-testid="explain-btn"
    >
      <HelpCircle className={size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />
      {size !== "sm" && <span className="text-[10px] font-mono uppercase tracking-wider">Explain</span>}
    </button>
  );
}

export function ExplainPanel({ open, onClose, data }) {
  const { i18n } = useAuth();
  if (!data) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg" data-testid="explain-panel">
        <DialogHeader>
          <DialogTitle className="font-barlow text-lg text-slate-50 flex items-center gap-2">
            <Info className="w-5 h-5 text-cyan-400" />
            {data.title || "Explanation"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <section>
            <h4 className="text-[10px] text-cyan-400 font-mono uppercase tracking-wider mb-1.5">{i18n("explain.whatIs")}</h4>
            <p className="text-sm text-slate-300 leading-relaxed">{data.what || "—"}</p>
          </section>
          {data.why && (
            <section>
              <h4 className="text-[10px] text-amber-400 font-mono uppercase tracking-wider mb-1.5">{i18n("explain.why")}</h4>
              <p className="text-sm text-slate-400 leading-relaxed">{data.why}</p>
            </section>
          )}
          {data.affects && data.affects.length > 0 && (
            <section>
              <h4 className="text-[10px] text-blue-400 font-mono uppercase tracking-wider mb-1.5">{i18n("explain.affects")}</h4>
              <div className="space-y-1">
                {data.affects.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                    <ChevronRight className="w-3 h-3 text-slate-600" />
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* What happens next */}
          {data.next && (
            <section>
              <h4 className="text-[10px] text-emerald-400 font-mono uppercase tracking-wider mb-1.5">{i18n("explain.next")}</h4>
              <p className="text-sm text-slate-400 leading-relaxed">{data.next}</p>
            </section>
          )}

          {/* Risk/Warning */}
          {data.risk && (
            <section className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-400 leading-relaxed">{data.risk}</p>
              </div>
            </section>
          )}

          {/* Breakdown items */}
          {data.breakdown && data.breakdown.length > 0 && (
            <section>
              <h4 className="text-[10px] text-slate-500 font-mono uppercase tracking-wider mb-1.5">Breakdown</h4>
              <div className="space-y-1.5">
                {data.breakdown.map((b, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30 border border-slate-700/20">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: b.color || "#94A3B8" }} />
                      <span className="text-xs text-slate-400">{b.label}</span>
                    </div>
                    <span className="text-xs font-mono text-slate-300">{b.value}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper to generate explain data for common objects
export function explainVault(vault, formatMoney) {
  const type = vault.vault_type;
  const perm = vault.rules?.permission;
  const descriptions = {
    TAX_CUSTODY: { what: "Money held for the tax authority. This is VAT/tax collected on behalf of the government — it was never yours.", risk: "If used before payment date, this creates a tax liability breach and potential penalties." },
    SUPPLIER_OBLIGATION: { what: "Funds reserved for supplier payments. This money is owed to your suppliers for goods/services received.", risk: "Using this risks supplier relationships and creditworthiness." },
    CUSTOMER_TRUST: { what: "Customer deposits held in trust. This money belongs to your customers until delivery conditions are met.", risk: "Spending this before delivery creates a trust violation and potential refund liability." },
    INVENTORY_CUSTODY: { what: "Value of inventory received but not yet paid for. This represents goods you have but haven't settled.", risk: "This value is custodial until supplier payment is made." },
    OPERATING_CUSTODY: { what: "Loan and overdraft proceeds. This capital is usable but not owned — it must be repaid.", risk: "Over-reliance increases repayment burden and insolvency risk." },
    FREE_EQUITY: { what: "Your true owned surplus. This is the only money that is genuinely yours to spend freely.", risk: null },
  };
  const d = descriptions[type] || { what: "Vault funds.", risk: null };
  return {
    title: vault.label,
    what: d.what,
    why: `Current balance: ${formatMoney(vault.balance)}. Permission level: ${perm}.`,
    affects: ["Ownership Truth Dashboard totals", "Obligation coverage ratios", "Operating runway calculation", "Behavioural discipline score"],
    next: type === "FREE_EQUITY" ? "Available for operations, investments, or distributions within buffer limits." : `Funds will be disbursed when ${type === "TAX_CUSTODY" ? "tax payment is due" : type === "SUPPLIER_OBLIGATION" ? "supplier invoices are settled" : type === "CUSTOMER_TRUST" ? "delivery conditions are satisfied" : "obligations are met"}.`,
    risk: d.risk,
  };
}

export function explainOwnership(label, amount, formatMoney) {
  const explanations = {
    "Owned Capital": { what: "True surplus belonging to the business. This is what remains after all obligations, custody, and restrictions are accounted for.", affects: ["Free Equity vault", "Dividend capacity", "Investment runway"], next: "Can be used for operations, growth, or distributions." },
    "Custody Funds": { what: "Money held on behalf of others — government (tax), suppliers (trade credit), and customers (deposits). This cash is in your bank but is NOT yours.", affects: ["Tax Custody vault", "Supplier Obligation vault", "Customer Trust vault", "Inventory Custody vault"], next: "Will be paid out as obligations fall due.", risk: "Spending custody funds creates obligation shortfalls." },
    "Restricted Capital": { what: "Loan proceeds and overdraft funds. Usable for operations but must be repaid — this is not profit.", affects: ["Operating Custody vault", "Loan repayment obligations", "Debt-to-equity ratio"], next: "Repayment reduces this balance over time.", risk: "High reliance on restricted capital increases insolvency risk." },
    "Assigned Funds": { what: "Receivables that have been factored or pledged as security. These funds are encumbered and not freely available.", affects: ["Factoring obligations", "Available liquidity"], next: "Released when factoring terms are fulfilled." },
  };
  const d = explanations[label] || { what: "Financial funds.", affects: [] };
  return { title: `${label}: ${formatMoney(amount)}`, ...d };
}
